import React, { useCallback, useState } from "react";
import {
  Upload,
  X,
  Image as ImageIcon,
  User,
  Camera,
  FileImage,
} from "lucide-react";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { cn } from "@/lib/utils";
import { useImageUpload } from "@/hooks/useImageUpload";
import { ImageDisplay } from "./image-display";
import { toast } from "sonner";

interface GenericImageUploadProps {
  value?: {
    key: string;
    filename: string;
    contentType?: string;
    size?: number;
    blurhash?: string;
  } | null;
  onChange: (
    image: {
      key: string;
      filename: string;
      contentType?: string;
      size?: number;
      blurhash?: string;
    } | null
  ) => void;
  uploadPath?: string;
  imageType: string;
  aspectRatio?: string;
  maxSize?: number; // in bytes
  accept?: string;
  disabled?: boolean;
  className?: string;
}

export function GenericImageUpload({
  value,
  onChange,
  uploadPath,
  imageType,
  aspectRatio,
  maxSize = 5 * 1024 * 1024, // 5MB default
  accept = "image/*",
  disabled = false,
  className,
}: GenericImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const {
    upload,
    isUploading,
    error: uploadError,
  } = useImageUpload(uploadPath);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxSize) {
        return `file size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`;
      }

      if (!file.type.startsWith("image/")) {
        return "only image files are allowed";
      }

      return null;
    },
    [maxSize]
  );

  const handleFile = useCallback(
    async (file: File) => {
      if (disabled) return;

      if (!uploadPath || uploadPath.trim() === "") {
        toast.error("Upload path is required before uploading images");
        return;
      }

      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }

      try {
        const result = await upload(file, imageType);
        onChange({
          key: result.key,
          filename: result.filename,
          contentType: result.contentType,
          size: result.size,
          blurhash: result.blurhash,
        });
      } catch (error) {
        // error handling is done in the hook
      }
    },
    [disabled, uploadPath, validateFile, upload, imageType, onChange]
  );

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      // only take the first file for single image upload
      handleFile(files[0]);
    },
    [handleFile]
  );

  const removeImage = useCallback(async () => {
    setIsRemoving(true);
    try {
      onChange(null);
      toast.success(`${imageType} image removed`);
    } catch (error) {
      toast.error("Failed to remove image");
    } finally {
      setIsRemoving(false);
    }
  }, [onChange, imageType]);

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (!disabled) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [disabled, handleFiles]
  );

  const onFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
    },
    [handleFiles]
  );

  // determine aspect ratio and icon based on imageType
  const getAspectRatio = () => {
    if (aspectRatio) return aspectRatio;

    switch (imageType) {
      case "profile":
        return "aspect-square";
      case "banner":
        return "aspect-[3/1]";
      case "cover":
        return "aspect-[16/9]";
      default:
        return "aspect-square";
    }
  };

  const getIcon = () => {
    switch (imageType) {
      case "profile":
        return User;
      case "banner":
        return Camera;
      case "cover":
        return FileImage;
      default:
        return ImageIcon;
    }
  };

  const getDisplayName = () => {
    switch (imageType) {
      case "profile":
        return "Profile";
      case "banner":
        return "Banner";
      case "cover":
        return "Cover";
      default:
        return imageType.charAt(0).toUpperCase() + imageType.slice(1);
    }
  };

  const getRecommendedSize = () => {
    switch (imageType) {
      case "profile":
        return "400x400px";
      case "banner":
        return "1200x400px";
      case "cover":
        return "1200x675px";
      default:
        return "400x400px";
    }
  };

  const aspectRatioClass = getAspectRatio();
  const uploadId = `image-${imageType}-upload`;
  const Icon = getIcon();

  return (
    <div className={cn("space-y-4", className)}>
      {value ? (
        // show uploaded image
        <div className="relative group">
          <div
            className={cn(
              "overflow-hidden rounded-lg border bg-muted",
              aspectRatioClass
            )}
          >
            <ImageDisplay
              imageKey={value.key}
              alt={`${getDisplayName()} image`}
              className="w-full h-full object-cover"
              fallback={
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Icon className="h-12 w-12 text-muted-foreground" />
                </div>
              }
            />
          </div>

          {!disabled && (
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="h-8 w-8"
                onClick={removeImage}
                disabled={isRemoving}
              >
                {isRemoving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <X className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}

          <p className="text-xs text-muted-foreground mt-2 truncate">
            {value.filename}
          </p>
        </div>
      ) : (
        // show upload area
        <Card
          className={cn(
            "border-2 border-dashed transition-colors cursor-pointer",
            isDragging && "border-primary bg-primary/5",
            disabled && "opacity-50 cursor-not-allowed",
            aspectRatioClass
          )}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() =>
            !disabled && document.getElementById(uploadId)?.click()
          }
        >
          <CardContent className="p-6 h-full flex items-center justify-center">
            <div className="flex flex-col items-center justify-center text-center">
              {isUploading ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                  <p className="text-sm text-muted-foreground">
                    Uploading {getDisplayName().toLowerCase()} image...
                  </p>
                </div>
              ) : !uploadPath || uploadPath.trim() === "" ? (
                <div className="flex flex-col items-center">
                  <Icon className="h-10 w-10 text-muted-foreground mb-4" />
                  <p className="text-sm font-medium text-muted-foreground">
                    Please provide an upload path first
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {getDisplayName()} image will be saved using the upload path
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="h-10 w-10 text-muted-foreground mb-4" />
                  <div className="mb-2">
                    <p className="text-sm font-medium">
                      Drag and drop {getDisplayName().toLowerCase()} image here,
                      or{" "}
                      <span className="text-primary underline hover:no-underline">
                        browse files
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Recommended: {getRecommendedSize()} (converted to JPG),
                      max {Math.round(maxSize / 1024 / 1024)}MB
                    </p>
                  </div>
                </div>
              )}

              <input
                id={uploadId}
                type="file"
                accept={accept}
                className="hidden"
                onChange={onFileInputChange}
                disabled={
                  disabled ||
                  !uploadPath ||
                  uploadPath.trim() === "" ||
                  isUploading
                }
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* error message */}
      {uploadError && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {uploadError}
        </div>
      )}
    </div>
  );
}
